import { Outlet, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Wallet, LayoutDashboard, ArrowLeftRight, Repeat, BarChart3, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

function AccountSwitcher() {
  const { user, setUser } = useAuth();
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [user?.account?.id]);

  const switchTo = async (accountId) => {
    if (accountId === user?.account?.id) return;
    const { data } = await api.post("/accounts/switch", { account_id: accountId });
    setUser(data);
  };

  if (accounts.length <= 1) {
    return <p data-testid="active-account-name" className="text-xs text-primary font-semibold truncate mb-2">{user?.account?.name}</p>;
  }
  return (
    <div className="mb-2">
      <Select value={user?.account?.id} onValueChange={switchTo}>
        <SelectTrigger data-testid="account-switcher" className="h-8 text-xs rounded-lg bg-secondary/50">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-card border-border">
          {accounts.map((a) => (
            <SelectItem key={a.account_id} value={a.account_id} data-testid={`account-option-${a.account_id}`}>
              {a.name} {a.role === "owner" ? "· Sahibi" : "· Üye"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const navItems = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/islemler", label: "İşlemler", icon: ArrowLeftRight, testId: "nav-transactions" },
  { to: "/sabitler", label: "Sabitler", icon: Repeat, testId: "nav-recurring" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, testId: "nav-reports" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testId: "nav-settings" },
];

export default function Layout() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card z-30">
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Wallet className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-heading font-bold tracking-tight leading-none">Usta Hesap</p>
            <p className="text-xs text-muted-foreground mt-1">Gelir & Gider Takibi</p>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 mt-4">
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }>
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-border">
          <AccountSwitcher />
          <p data-testid="sidebar-user-name" className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
          <Wallet className="w-4 h-4 text-primary-foreground" />
        </div>
        <p className="font-heading font-bold tracking-tight">Usta Hesap</p>
      </header>

      <main className="md:ml-64 p-4 md:p-8 pb-24 md:pb-8" key={user?.account?.id}>
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex">
        {navItems.map(({ to, label, icon: Icon, testId }) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`${testId}-mobile`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-xs transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }>
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
